import { useState, useRef, useEffect } from "react";

const userGroups = [
  {
    id: "G1",
    name: "高价值用户",
    count: 312,
    color: "bg-green-500",
    textColor: "text-green-600",
    bg: "bg-green-50",
    border: "border-green-100",
    desc: "月均用气 &gt;40m³，缴费及时，安全等级A+",
    icon: "ri-vip-crown-line",
    avgUsage: 52.3,
    avgBalance: 486.5,
    riskScore: 8,
  },
  {
    id: "G2",
    name: "普通用户",
    count: 685,
    color: "bg-cyan-500",
    textColor: "text-cyan-600",
    bg: "bg-cyan-50",
    border: "border-cyan-100",
    desc: "月均用气 15-40m³，缴费正常，安全等级A/B",
    icon: "ri-user-3-line",
    avgUsage: 26.8,
    avgBalance: 215.3,
    riskScore: 25,
  },
  {
    id: "G3",
    name: "欠费风险用户",
    count: 156,
    color: "bg-amber-500",
    textColor: "text-amber-600",
    bg: "bg-amber-50",
    border: "border-amber-100",
    desc: "余额低于100元或连续2月未缴费",
    icon: "ri-error-warning-line",
    avgUsage: 18.2,
    avgBalance: 45.6,
    riskScore: 72,
  },
  {
    id: "G4",
    name: "高风险用户",
    count: 43,
    color: "bg-red-500",
    textColor: "text-red-600",
    bg: "bg-red-50",
    border: "border-red-100",
    desc: "安检逾期、设备异常或欠费超3个月",
    icon: "ri-shield-cross-line",
    avgUsage: 12.5,
    avgBalance: 12.3,
    riskScore: 91,
  },
];

const riskUsers = [
  { name: "李秀梅", id: "R002", balance: 89.5, overdue: 2, risk: 78, phone: "136****5432" },
  { name: "孙志远", id: "R007", balance: 88.3, overdue: 1, risk: 65, phone: "138****2345" },
  { name: "周晓燕", id: "R008", balance: 45.2, overdue: 3, risk: 85, phone: "186****3456" },
  { name: "吴建军", id: "R012", balance: 23.8, overdue: 4, risk: 92, phone: "139****4567" },
  { name: "郑美华", id: "R015", balance: 12.1, overdue: 5, risk: 96, phone: "135****5678" },
];

const portraitData = {
  cookingFreq: [
    { label: "1次/天", pct: 18 },
    { label: "2次/天", pct: 35 },
    { label: "3次/天", pct: 32 },
    { label: "4次+/天", pct: 15 },
  ],
  peakHours: [
    { hour: "6-8时", val: 65 },
    { hour: "8-10时", val: 25 },
    { hour: "10-12时", val: 30 },
    { hour: "12-14时", val: 55 },
    { hour: "14-16时", val: 15 },
    { hour: "16-18时", val: 20 },
    { hour: "18-20时", val: 88 },
    { hour: "20-22时", val: 42 },
  ],
  appliances: [
    { name: "双眼灶", pct: 68 },
    { name: "热水器", pct: 82 },
    { name: "壁挂炉", pct: 45 },
    { name: "三眼灶", pct: 22 },
    { name: "烤箱", pct: 12 },
  ],
};

export default function UserPortraitPanel() {
  const [activeTab, setActiveTab] = useState<"group" | "portrait" | "risk" | "urge">("group");
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [urging, setUrging] = useState(false);
  const [urgeSuccess, setUrgeSuccess] = useState(false);
  const [selectedRiskUsers, setSelectedRiskUsers] = useState<string[]>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (activeTab !== "portrait") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    // Draw peak hours bar chart
    const bars = portraitData.peakHours;
    const maxVal = Math.max(...bars.map(b => b.val));
    const barW = (w - 40) / bars.length;
    const chartH = h - 40;

    bars.forEach((b, i) => {
      const x = 20 + i * barW;
      const bh = (b.val / maxVal) * chartH;
      const y = chartH - bh;

      const grad = ctx.createLinearGradient(x, y, x, chartH);
      grad.addColorStop(0, "rgba(232,83,26,0.9)");
      grad.addColorStop(1, "rgba(232,83,26,0.2)");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.roundRect(x + barW * 0.1, y, barW * 0.8, bh, 2);
      ctx.fill();

      ctx.fillStyle = "rgba(100,100,100,0.7)";
      ctx.font = "8px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(b.hour, x + barW / 2, h - 5);
    });
  }, [activeTab]);

  const handleUrge = () => {
    setUrging(true);
    setTimeout(() => {
      setUrging(false);
      setUrgeSuccess(true);
      setTimeout(() => setUrgeSuccess(false), 3000);
    }, 2000);
  };

  const toggleRiskUser = (id: string) => {
    setSelectedRiskUsers(prev => prev.includes(id) ? prev.filter(u => u !== id) : [...prev, id]);
  };

  return (
    <div className="bg-gradient-to-r from-[#0f1729] to-[#1a2540] rounded-xl border border-white/10">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-[#E8531A]/20">
            <i className="ri-user-heart-line text-[#E8531A] text-sm"></i>
          </div>
          <span className="text-white text-sm font-semibold">用户画像 &amp; 智能分析</span>
          <span className="flex items-center gap-1 text-xs text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse inline-block"></span>
            AI实时分析
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white/5 rounded-lg p-1 mx-4 mt-3">
        {[
          { key: "group", label: "用户分群", icon: "ri-group-line" },
          { key: "portrait", label: "行为画像", icon: "ri-pie-chart-line" },
          { key: "risk", label: "欠费风险", icon: "ri-error-warning-line" },
          { key: "urge", label: "AI智能催缴", icon: "ri-robot-2-line" },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key as typeof activeTab)}
            className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${activeTab === t.key ? "bg-white/15 text-white" : "text-white/50 hover:text-white/70"}`}
          >
            <i className={t.icon}></i>{t.label}
          </button>
        ))}
      </div>

      <div className="p-4">
        {/* User Groups */}
        {activeTab === "group" && (
          <div>
            <div className="grid grid-cols-4 gap-3 mb-4">
              {userGroups.map(g => (
                <div
                  key={g.id}
                  onClick={() => setSelectedGroup(selectedGroup === g.id ? null : g.id)}
                  className={`${g.bg} ${g.border} border rounded-xl p-3 cursor-pointer transition-all ${selectedGroup === g.id ? "ring-2 ring-[#E8531A]/40" : ""}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-6 h-6 flex items-center justify-center rounded-lg ${g.color}/20`}>
                      <i className={`${g.icon} ${g.textColor} text-xs`}></i>
                    </div>
                    <span className={`text-xs font-semibold ${g.textColor}`}>{g.name}</span>
                  </div>
                  <div className={`text-2xl font-bold ${g.textColor} mb-1`}>{g.count}</div>
                  <p className="text-[10px] text-gray-500 leading-relaxed" dangerouslySetInnerHTML={{ __html: g.desc }}></p>
                  <div className="mt-2 flex items-center justify-between text-[10px]">
                    <span className="text-gray-400">风险评分</span>
                    <span className={`font-bold ${g.riskScore > 70 ? "text-red-500" : g.riskScore > 40 ? "text-amber-500" : "text-green-500"}`}>{g.riskScore}</span>
                  </div>
                  <div className="w-full bg-white/60 rounded-full h-1 mt-1">
                    <div
                      className={`h-1 rounded-full ${g.riskScore > 70 ? "bg-red-500" : g.riskScore > 40 ? "bg-amber-500" : "bg-green-500"}`}
                      style={{ width: `${g.riskScore}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
            {selectedGroup && (
              <div className="bg-white/10 rounded-xl p-3 border border-white/10">
                {(() => {
                  const g = userGroups.find(x => x.id === selectedGroup)!;
                  return (
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-white text-sm font-semibold">{g.name}</span>
                        <span className="text-white/50 text-xs ml-2">共 {g.count} 户</span>
                      </div>
                      <div className="flex gap-2">
                        <div className="text-center">
                          <div className="text-white text-sm font-bold">{g.avgUsage} m³</div>
                          <div className="text-white/40 text-[10px]">月均用气</div>
                        </div>
                        <div className="text-center">
                          <div className="text-white text-sm font-bold">¥{g.avgBalance}</div>
                          <div className="text-white/40 text-[10px]">平均余额</div>
                        </div>
                        <button className="flex items-center gap-1 bg-[#E8531A] text-white px-3 py-1.5 rounded-lg text-xs cursor-pointer whitespace-nowrap">
                          <i className="ri-message-2-line"></i>批量通知
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* Behavior Portrait */}
        {activeTab === "portrait" && (
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <p className="text-white/60 text-xs mb-2">用气高峰时段分布</p>
              <canvas ref={canvasRef} width={400} height={120} className="w-full rounded-xl bg-white/5" />
            </div>
            <div>
              <p className="text-white/60 text-xs mb-2">用气设备分布</p>
              <div className="space-y-2">
                {portraitData.appliances.map((a, i) => (
                  <div key={i}>
                    <div className="flex justify-between text-[10px] mb-0.5">
                      <span className="text-white/60">{a.name}</span>
                      <span className="text-white/80">{a.pct}%</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-1.5">
                      <div className="bg-[#E8531A] h-1.5 rounded-full" style={{ width: `${a.pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-span-3 grid grid-cols-4 gap-3">
              {portraitData.cookingFreq.map((c, i) => (
                <div key={i} className="bg-white/5 rounded-xl p-3 text-center">
                  <div className="text-lg font-bold text-[#E8531A]">{c.pct}%</div>
                  <div className="text-[10px] text-white/50 mt-0.5">{c.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Risk Assessment */}
        {activeTab === "risk" && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-white/60 text-xs">AI识别高风险欠费用户（共 {riskUsers.length} 户）</span>
              <button
                onClick={() => setSelectedRiskUsers(riskUsers.map(u => u.id))}
                className="text-xs text-[#E8531A] cursor-pointer whitespace-nowrap"
              >
                全选
              </button>
            </div>
            <div className="space-y-2">
              {riskUsers.map(u => (
                <div
                  key={u.id}
                  onClick={() => toggleRiskUser(u.id)}
                  className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${selectedRiskUsers.includes(u.id) ? "border-[#E8531A]/40 bg-[#E8531A]/10" : "border-white/10 bg-white/5"}`}
                >
                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${selectedRiskUsers.includes(u.id) ? "border-[#E8531A] bg-[#E8531A]" : "border-white/30"}`}>
                    {selectedRiskUsers.includes(u.id) && <i className="ri-check-line text-white text-[10px]"></i>}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-white text-xs font-semibold">{u.name}</span>
                      <span className="text-white/40 text-[10px]">{u.id}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-[10px]">
                      <span className="text-red-400">余额 ¥{u.balance}</span>
                      <span className="text-amber-400">逾期 {u.overdue} 月</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-bold ${u.risk > 80 ? "text-red-400" : "text-amber-400"}`}>{u.risk}%</div>
                    <div className="text-[10px] text-white/40">欠费概率</div>
                  </div>
                </div>
              ))}
            </div>
            {selectedRiskUsers.length > 0 && (
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => setActiveTab("urge")}
                  className="flex-1 bg-[#E8531A] text-white py-2 rounded-lg text-xs cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-robot-2-line mr-1"></i>AI智能催缴 ({selectedRiskUsers.length}户)
                </button>
                <button className="flex items-center gap-1 border border-white/20 text-white/70 px-3 py-2 rounded-lg text-xs cursor-pointer whitespace-nowrap">
                  <i className="ri-message-2-line"></i>短信通知
                </button>
              </div>
            )}
          </div>
        )}

        {/* AI Urge */}
        {activeTab === "urge" && (
          <div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[
                { label: "短信催缴", icon: "ri-message-2-line", desc: "发送个性化催缴短信", rate: "68%响应率", color: "text-cyan-400" },
                { label: "电话外呼", icon: "ri-phone-line", desc: "AI语音外呼催缴", rate: "52%响应率", color: "text-green-400" },
                { label: "银行代扣", icon: "ri-bank-card-line", desc: "自动银行代扣", rate: "95%成功率", color: "text-[#E8531A]" },
              ].map((m, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-3 text-center">
                  <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/10 mx-auto mb-2">
                    <i className={`${m.icon} ${m.color} text-sm`}></i>
                  </div>
                  <div className="text-white text-xs font-semibold mb-1">{m.label}</div>
                  <div className="text-white/40 text-[10px] mb-1">{m.desc}</div>
                  <div className={`text-[10px] font-bold ${m.color}`}>{m.rate}</div>
                </div>
              ))}
            </div>

            <div className="bg-white/5 rounded-xl p-3 mb-3 border border-white/10">
              <p className="text-white/60 text-xs mb-2">AI催缴策略配置</p>
              <div className="space-y-2">
                {[
                  { label: "催缴方式", options: ["短信+电话", "仅短信", "仅电话", "银行代扣"] },
                  { label: "催缴时间", options: ["工作日9-18时", "全天", "周末"] },
                  { label: "催缴频率", options: ["每3天一次", "每周一次", "每天一次"] },
                ].map((cfg, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-white/50 text-[10px] w-16 flex-shrink-0">{cfg.label}</span>
                    <select className="flex-1 bg-white/10 border border-white/20 rounded-lg px-2 py-1 text-[10px] text-white focus:outline-none">
                      {cfg.options.map(o => <option key={o} className="bg-gray-800">{o}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            {urgeSuccess ? (
              <div className="flex items-center gap-2 bg-green-500/20 border border-green-500/30 rounded-xl p-3">
                <i className="ri-checkbox-circle-line text-green-400"></i>
                <span className="text-green-400 text-xs">AI催缴任务已启动！已向 {riskUsers.length} 户用户发送催缴通知</span>
              </div>
            ) : (
              <button
                onClick={handleUrge}
                disabled={urging}
                className="w-full bg-[#E8531A] text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer hover:bg-[#d44a15] disabled:opacity-60 whitespace-nowrap"
              >
                {urging ? (
                  <><i className="ri-loader-4-line animate-spin mr-1"></i>AI正在执行催缴...</>
                ) : (
                  <><i className="ri-robot-2-line mr-1"></i>启动AI智能催缴（{riskUsers.length}户）</>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
